import { instanceBo, transformAxiosError } from "../utils/axios.create";
import {
	IAnalyte,
	AnalytesResponse,
	AnalyteType,
	AnalyteStatus,
} from "@/domain/analyte.interface";

export interface AnalyteRequest {
	accountId: string;
	shortname: string;
	shortnameDisplay: string;
	scientificName: string;
	scientificDisplay: string;
	type: AnalyteType;
	status: AnalyteStatus;
	createdBy: string;
}

export interface AnalyteUpdateRequest
	extends Omit<AnalyteRequest, "createdBy"> {
	_id: string;
	updatedBy: string;
}

export interface AnalyteSearchParams {
	search: string;
	sortField: string;
	sortOrder: number;
	pageNumber: number;
	pageSize: number;
}

export interface SearchAnalyteResponse {
	analytes: IAnalyte[];
	totalCount: number;
}

const getAnalytes = async (): Promise<AnalytesResponse> => {
	return instanceBo
		.get("/analytes")
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const getAnalyteById = async (id: string): Promise<IAnalyte> => {
	return instanceBo
		.get(`/analytes/${id}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const searchAnalytes = async ({
	search,
	sortField,
	sortOrder,
	pageNumber,
	pageSize,
}: AnalyteSearchParams): Promise<SearchAnalyteResponse> => {
	const queryParams = new URLSearchParams({
		pageNumber: String(pageNumber + 1),
		pageSize: String(pageSize),
		search,
		sortField,
		sortOrder: sortOrder === 1 ? "asc" : "desc",
	});

	return instanceBo
		.get(`/analytes/search?${queryParams.toString()}`)
		.then((res) => {
			const body = res.data.body;
			return {
				analytes: body.analytes,
				totalCount: body.totalCount,
			};
		})
		.catch((err) => {
			return Promise.reject(transformAxiosError(err));
		});
};

const createAnalyte = async (data: AnalyteRequest): Promise<IAnalyte> => {
	return instanceBo
		.post("/analytes", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updateAnalyte = async (data: AnalyteUpdateRequest): Promise<IAnalyte> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/analytes/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deleteAnalyte = async (id: string): Promise<void> => {
	return instanceBo
		.delete(`/analytes/${id}`)
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const analyteService = {
	getAnalytes,
	getAnalyteById,
	searchAnalytes,
	createAnalyte,
	updateAnalyte,
	deleteAnalyte,
};
